export function getUnixTime(date) {
  return Math.floor(date.getTime() / 1000);
}

export function getFromUnixTime(time) {
  return new Date(time * 1000);
}

export async function getStringFromURL(url, access_token, signal) {
  const headers = new Headers({
    "Cache-Control": "no-cache",
  });
  if (access_token !== undefined) {
    headers.append("Authorization", `Bearer ${access_token}`);
  }

  try {
    const response = await fetch(url, { method: "GET", headers, signal });
    if (!response.ok) {
      // Try to get error details from response body
      let errorDetails = response.statusText;
      try {
        const errorBody = await response.text();
        if (errorBody) {
          console.error("[Utilities] Server error response:", errorBody);
          errorDetails = `${response.statusText}: ${errorBody}`;
        }
      } catch (e) {
        // Ignore if can't read body
      }
      throw new Error(errorDetails);
    }
    return await response.text();
  } catch (error) {
    throw new Error(`Network error or CORS issue. Status: ${error.message}`);
  }
}

export async function deleteFromURL(url, access_token) {
  const headers = new Headers();
  if (access_token !== undefined) {
    headers.append("Authorization", `Bearer ${access_token}`);
  }

  try {
    const response = await fetch(url, { method: "DELETE", headers });
    if (!response.ok) {
      throw new Error(response.statusText);
    }
    return await response.text();
  } catch (error) {
    throw new Error(error.message);
  }
}

export async function postJsonToURL(
  url,
  json,
  access_token,
  { timeoutMs = 15000 } = {},
) {
  const headers = new Headers({
    "Cache-Control": "no-cache",
    "Content-Type": "application/json",
  });
  if (access_token !== undefined) {
    headers.append("Authorization", `Bearer ${access_token}`);
  }

  try {
    const response = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify(json),
      signal: AbortSignal.timeout(timeoutMs),
    });
    if (!response.ok) {
      throw new Error(response.statusText);
    }
    return await response.text();
  } catch (error) {
    if (error.name === "TimeoutError" || error.name === "AbortError") {
      throw new Error(`Request timed out after ${timeoutMs}ms: ${url}`);
    }
    throw new Error(error.message);
  }
}

export async function checkPublicConnectivity() {
  const publicUrl =
    import.meta.env.VITE_PUBLIC_CONNECTIVITY_CHECK_URL ||
    "https://www.google.com";

  try {
    await fetch(publicUrl, {
      method: "HEAD", // Use HEAD to minimize data transfer
      mode: "no-cors", // Allow cross-origin requests
      cache: "no-cache",
      signal: AbortSignal.timeout(5000), // 5 second timeout
    });
    return true; // If we get here, connectivity is working
  } catch (error) {
    console.log("Public connectivity check failed:", error.message);
    return false; // Public server is not reachable
  }
}

export function errorHandler(error) {
  // Check public connectivity first to determine the type of error
  checkPublicConnectivity()
    .then((hasPublicConnectivity) => {
      createConnectivityToast(error, hasPublicConnectivity);
    })
    .catch(() => {
      // If connectivity check fails, assume it's a general connectivity issue
      createConnectivityToast(error, false);
    });
}

function createConnectivityToast(error, hasPublicConnectivity) {
  // Create a toast notification instead of alert
  const toastContainer = document.createElement("div");
  toastContainer.id = "signalr-error-toast";
  toastContainer.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #dc3545;
        color: white;
        padding: 12px 16px;
        border-radius: 6px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        z-index: 10000;
        max-width: 400px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: 14px;
        line-height: 1.4;
    `;

  // Format error message based on error type and connectivity status
  let errorMessage = "Erro de conexão";
  let errorDetails = "";

  if (error && typeof error === "object") {
    if (error.message) {
      if (error.message.includes("WebSocket closed with status code: 1006")) {
        errorMessage = "Conexão perdida com o servidor";
        errorDetails =
          "A conexão foi interrompida. Tentando reconectar automaticamente...";
      } else if (
        error.message.includes("Failed to fetch") ||
        error.message.includes("NetworkError")
      ) {
        if (hasPublicConnectivity) {
          // Internet is working, so it's likely our server that's down
          errorMessage = "Servidor indisponível";
          errorDetails =
            "Nosso servidor está temporariamente fora do ar. Tente novamente em alguns minutos.";
        } else {
          // Internet connectivity issue
          errorMessage = "Problema de conectividade";
          errorDetails = "Verifique sua conexão com a internet.";
        }
      } else if (
        error.message.includes("401") ||
        error.message.includes("Unauthorized")
      ) {
        errorMessage = "Erro de autenticação";
        errorDetails = "Credenciais inválidas ou expiradas.";
      } else {
        errorDetails = error.message;
      }
    } else if (error.toString) {
      errorDetails = error.toString();
    }
  } else if (typeof error === "string") {
    errorDetails = error;
  }

  toastContainer.innerHTML = `
        <div style="font-weight: 600; margin-bottom: 4px;">${errorMessage}</div>
        <div style="opacity: 0.9; font-size: 13px;">${errorDetails}</div>
        <button onclick="this.parentElement.remove()" style="
            position: absolute;
            top: 8px;
            right: 8px;
            background: none;
            border: none;
            color: white;
            cursor: pointer;
            font-size: 16px;
            opacity: 0.7;
        ">×</button>
    `;

  // Remove any existing toast
  const existingToast = document.getElementById("signalr-error-toast");
  if (existingToast) {
    existingToast.remove();
  }

  document.body.appendChild(toastContainer);

  // Auto-remove after 8 seconds
  setTimeout(() => {
    if (toastContainer.parentElement) {
      toastContainer.remove();
    }
  }, 8000);

  // Also log to console for debugging
  console.error("SignalR Error:", error, { hasPublicConnectivity });
}

export function getParameterByName(name) {
  name = name.replace(/[[]/g, "[").replace(/[\]]/g, "]");
  const regex = new RegExp(`[\\?&]${name}=([^&#]*)`);
  const results = regex.exec(window.location.search);
  return results === null
    ? ""
    : decodeURIComponent(results[1].replace(/\+/g, " "));
}

let loadJSReferences = []; //list of files already added

function loadJSFILE(filename, filetype) {
  let fileref;
  if (filetype === "js") {
    //if filename is a external JavaScript file
    fileref = document.createElement("script");
    fileref.type = "text/javascript";
    fileref.src = filename;
  } else if (filetype === "css") {
    //if filename is an external CSS file
    fileref = document.createElement("link");
    fileref.rel = "stylesheet";
    fileref.type = "text/css";
    fileref.href = filename;
  }
  if (fileref) {
    document.head.appendChild(fileref);
  }
}

export function loadJS(filename, filetype) {
  if (!loadJSReferences.includes(filename)) {
    loadJSFILE(filename, filetype);
    loadJSReferences.push(filename);
  }
}

export function removeJS(filename, filetype) {
  const idx = loadJSReferences.indexOf(filename);
  if (idx !== -1) {
    loadJSReferences.splice(idx, 1);
  }
  const targetelement = filetype === "js" ? "script" : "link";
  const targetattr = filetype === "js" ? "src" : "href";
  const allsuspects = Array.from(document.getElementsByTagName(targetelement));

  allsuspects.forEach((suspect) => {
    if (
      suspect.getAttribute(targetattr) !== null &&
      suspect.getAttribute(targetattr).includes(filename)
    ) {
      suspect.parentNode.removeChild(suspect); //remove element by calling parentNode.removeChild()
    }
  });
}
